import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DataGrid from "@dtci/spellbook/DataGrid";
import styles from "./pptPromosGrid.module.scss";
import tableStyles from "../../../../components/AGGrid/details-table.module.scss";
import { usePPTState } from "tap/store/ppt/usePPT";
import {
  useAgGridExternalFilter,
  useColumnStatePreferences,
} from "tap/components/AGGrid/hooks";
import { COLUMN_STATE_STORAGE_KEYS } from "tap/utils/gridColumnPreferences";
import { promoMatchesTileFilter } from "ppt/utilities";
import { defaultColumnDef, columnDefs } from "./config/columns";

// Module-level so the object identity never changes. Everything inside the
// `agGrid` prop has to be stable: ag-grid-react diffs its props by reference and
// re-applies whatever looks changed, and a fresh `sideBar` object on every
// render tears down and rebuilds the tool panels — closing the one the user had
// open — as well as raising column events that used to trigger an auto-save.
const SIDE_BAR = {
  toolPanels: [
    "filters",
    {
      id: "columns",
      labelDefault: "Columns",
      labelKey: "columns",
      iconKey: "columns",
      toolPanel: "agColumnsToolPanel",
      toolPanelParams: {
        suppressPivotMode: true,
        suppressRowGroups: true,
        suppressValues: true,
      },
    },
  ],
};

export const PPTPromosGrid = ({
  gridRef,
  initialState,
  onStateUpdated,
  onLeaveForDetail,
  onColumnStateChange,
}) => {
  const { promos, promosSearch, promosIssueFilter } = usePPTState();
  const navigate = useNavigate();

  // Persist column customization (width, visibility, order) with auto-save
  const {
    initialGridState,
    isLayoutRestored,
    resetColumnState,
    hasSavedColumnState,
    isGridReady,
  } = useColumnStatePreferences(
    COLUMN_STATE_STORAGE_KEYS.promosGrid,
    gridRef,
    columnDefs,
  );

  // Kept out of sight until the saved widths are on it. The grid has to be
  // mounted for those to be applied at all, so this hides it in place rather
  // than holding it back — the page's skeleton can't help here, since it doesn't
  // mount its children and the grid would never get far enough to be corrected.
  // Only users with a saved layout ever see this; for everyone else
  // isLayoutRestored is true from the first render.
  const gridClassName = [
    tableStyles.dataGrid,
    styles.pptPromosGrid,
    isLayoutRestored ? null : tableStyles.awaitingLayout,
  ]
    .filter(Boolean)
    .join(" ");

  // Two independent sources of initial state, and AG Grid takes exactly one
  // object: sort and filter come from the URL (see useAgGridUrlState), column
  // order, visibility and pinning from the user's saved preferences. Neither
  // writes the other's keys, so a shallow merge is the whole reconciliation.
  //
  // Memoized so the reference survives re-renders. AG Grid reads initialState
  // once, while it builds the grid, and silently ignores it afterwards — so a new
  // object each render would be dead weight fed through ag-grid-react's
  // reference diff. Both inputs are themselves stable: the page snapshots the URL
  // state once, and the hook snapshots the preferences once they load.
  const gridInitialState = useMemo(() => {
    if (!initialGridState) return initialState;
    return { ...initialState, ...initialGridState };
  }, [initialState, initialGridState]);

  // Pass column state controls up to parent. Readiness travels with them because
  // the page's Reset control lives outside the skeleton that holds this grid, so
  // it is on screen before there is any grid for it to reset, and so is the only
  // thing that knows whether there is a saved layout to reset.
  useEffect(() => {
    if (onColumnStateChange) {
      onColumnStateChange({
        resetColumnState,
        hasSavedColumnState,
        isGridReady,
      });
    }
  }, [onColumnStateChange, resetColumnState, hasSavedColumnState, isGridReady]);

  const onRowClicked = useCallback(
    (event) => {
      // Save the list view first so returning from the detail page restores it.
      onLeaveForDetail?.();
      navigate(`/ppt/promotion/${event.data.id}`);
    },
    [navigate, onLeaveForDetail],
  );

  // Quick-filter driven by the stat tiles (see PPTPromosStatTiles). The bucketing
  // rule lives in ppt/utilities so the tile counts and these rows agree.
  const matchesTileFilter = useCallback(
    (rowData) => promoMatchesTileFilter(rowData, promosIssueFilter),
    [promosIssueFilter],
  );
  const { isExternalFilterPresent, doesExternalFilterPass } =
    useAgGridExternalFilter(gridRef, promosIssueFilter, matchesTileFilter);

  const getRowId = useCallback(
    (params) => String(params.data?.id || params.data?.name),
    [],
  );

  // Memoized because the Spellbook wrapper feeds this straight into the useMemo
  // that builds AG Grid's options, and ag-grid-react then re-applies every
  // option whose reference changed. Rebuilt on each render, it made a single
  // re-render — an auto-save's optimistic Redux write, say — re-initialize the
  // grid's options, rebuild the side bar and re-run flex sizing.
  const agGridProps = useMemo(
    () => ({
      ref: gridRef,
      rowData: promos,
      resetRowDataOnUpdate: true,
      headerHeight: 45,
      suppressContextMenu: true,
      quickFilterText: promosSearch,
      includeHiddenColumnsInQuickFilter: true,
      functionsReadOnly: false,
      suppressMovableColumns: false,
      sideBar: SIDE_BAR,
      getRowId,
      onRowClicked,
      onStateUpdated,
      isExternalFilterPresent,
      doesExternalFilterPass,
    }),
    [
      gridRef,
      promos,
      promosSearch,
      getRowId,
      onRowClicked,
      onStateUpdated,
      isExternalFilterPresent,
      doesExternalFilterPass,
    ],
  );

  return (
    <>
      <DataGrid
        className={gridClassName}
        autoSizing
        deltaRowDataMode={true}
        agGrid={agGridProps}
        bordered="none"
        // Top-level, not inside agGrid: the DataGrid wrapper overrides
        // agGrid.initialState with this prop.
        initialState={gridInitialState}
        columnDefs={columnDefs}
        defaultColumnDef={defaultColumnDef}
        noResultsMessage={"No Promos Found"}
        // "No Promos Found" should mean "there are no promos", not "your filters
        // matched nothing" — so the overlay tracks the size of the dataset, not
        // the filtered row count.
        //
        // Without this the DataGrid wrapper decides the overlay from
        // `getDisplayedRowCount()` in an effect that only depends on
        // [api, isFetching, totalCount] — so it runs once at grid-ready and the
        // verdict sticks forever. Arriving on a URL whose filters match nothing
        // left the message up even after the filters were cleared and rows were
        // visible behind it; arriving unfiltered and then filtering to zero
        // showed no message at all. Passing totalCount short-circuits that row
        // count and, being a dependency, re-evaluates whenever promos change.
        totalCount={promos.length}
        dataMode="client"
        filterable
        sortable
      />
    </>
  );
};
